<script setup lang="ts">
import { computed, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import VuePassword from "vue-password";
import { VueTelInput } from "vue-tel-input";

import { debounce } from "lodash";
import moment from "moment-timezone";
import "vue-tel-input/vue-tel-input.css";

import DatePicker from "@/dsComponents/DatePicker/DatePicker.vue";

import CurrencyInput from "@/components/CurrencyInput.vue";
import PasswordStrength from "@/components/PasswordStrength.vue";
import VueTimepicker from "@/components/VueTimepicker.vue";

import NewIcon from "../NewIcon.vue";
import ErrorComponent from "./Error.vue";

const props = withDefaults(
    defineProps<{
        // biome-ignore lint/suspicious/noExplicitAny: Init Biome
        modelValue: any;
        name?: string;
        type?: string;
        label?: string;
        templateLabel?: string;
        placeholder?: string;
        // biome-ignore lint/suspicious/noExplicitAny: Init Biome
        error?: any;
        // biome-ignore lint/suspicious/noExplicitAny: Init Biome
        options?: any[];
        size?: number;
        required?: boolean;
        readonly?: boolean;
        // biome-ignore lint/suspicious/noExplicitAny: Init Biome
        passwordStrength?: any;
        labelIcon?: string;
        icon?: string;
        inputWidth?: string;
        dateMin?: number;
        dateMax?: number;
        timeStep?: number;
        autocomplete?: boolean;
        inlineDate?: boolean;
        disabled?: boolean;
        mask?: string;
        contractManagerBlocked?: boolean;
        contractManagerBlockedNoWarning?: boolean;
        debounceTime?: number;
    }>(),
    {
        type: "text",
        debounceTime: 0,
    }
);

const emit = defineEmits<{
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    (e: "update:modelValue", value: any): void;
    // biome-ignore lint/suspicious/noExplicitAny: Init Biome
    (e: "change", value: any): void;
    (e: "blur"): void;
    (e: "focus"): void;
    (e: "enter"): void;
}>();

const { t } = useI18n();

const groupClass = computed(() => {
    return [{ "form-check": props.type === "checkbox" }, { "form-rad": props.type === "radio" }, { "form-error": props.error }];
});

const theLabel = computed(() => {
    if (props.required) {
        return `${props.label}<span style="color: #2051F8;"> *</span>`;
    } else {
        return props.label;
    }
});

const debouncedEmitInput = debounce((value) => {
    emit("update:modelValue", value);
}, props.debounceTime);

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const emitInput = (value: any) => {
    debouncedEmitInput(value);
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const changed = (e: any) => {
    // biome-ignore lint/complexity/useOptionalChain: Init Biome
    if (e && e.target) {
        emitInput(e.target?.value);
        nextTick(() => {
            emit("change", e.target?.value);
        });
    } else {
        emitInput(e);
        nextTick(() => {
            emit("change", e);
        });
    }
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const onPassword = (password: any) => {
    emit("update:modelValue", password);
    nextTick(() => {
        emit("change", password);
    });
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const checked = (e: any) => {
    emit("update:modelValue", e.target.checked);
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const radiohead = (e: any) => {
    emit("update:modelValue", e.target.value);
};

const blurEvent = () => {
    emit("blur");
};
const focusEvent = () => {
    emit("focus");
};

const dateChanged = (e: string) => {
    emit("update:modelValue", e);
    nextTick(() => {
        emit("change", e);
    });
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
// biome-ignore lint/correctness/noUnusedVariables: Init Biome
const txtDateChanged = (e: any) => {
    // biome-ignore lint/complexity/useOptionalChain: Init Biome
    if (e && e.target) {
        emit("update:modelValue", moment(e.target.value).valueOf());
        nextTick(() => {
            emit("change", moment(e.target.value).valueOf());
        });
    }
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const timeChanged = (e: any) => {
    emit("update:modelValue", e);
    nextTick(() => {
        emit("change", e);
    });
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const telChanged = (e: any) => {
    emit("update:modelValue", e);
    nextTick(() => {
        emit("change", e);
    });
};

// biome-ignore lint/suspicious/noExplicitAny: Init Biome
const priceChanged = (e: any) => {
    emit("update:modelValue", e);
    nextTick(() => {
        emit("change", e);
    });
};
</script>

<template>
    <div v-if="type === 'checkbox'" :class="groupClass">
        <input type="checkbox" class="form-checkbox" :id="name" :name="name" @change="checked" :checked="modelValue" @blur="blurEvent" />
        <label v-if="label" class="form-check-label" :for="name">{{ label }}</label>
    </div>
    <div v-else-if="type === 'radio'" :class="groupClass">
        <input type="radio" class="form-radio" :id="modelValue" :name="name" @change="radiohead" :value="modelValue" @blur="blurEvent" />
        <label v-if="label" class="form-radio-label" :for="modelValue">{{ label }}</label>
    </div>
    <div v-else :class="groupClass">
        <div class="input">
            <div class="label" v-if="labelIcon">
                <NewIcon :icon="labelIcon" />
                <label v-if="label" :for="name" v-html="theLabel" />
            </div>
            <label v-if="!labelIcon && label && type !== 'template-area'" :for="name" v-html="theLabel" />
            <icon v-if="icon" :icon="icon" />
            <div
                :class="`value ${(contractManagerBlocked ? 'cm-blocked' : '')}`"
                v-if="type === 'read' && contractManagerBlocked"
                v-html="modelValue || 'Non renseigné'"
            />
            <div v-if="contractManagerBlocked && !contractManagerBlockedNoWarning" class="cm-blocked-explanation">
                {{ t("global.contractManager.blockedField") }}
            </div>
            <template v-if="!contractManagerBlocked">
                <div
                    :class="`value ${(contractManagerBlocked ? 'cm-blocked' : '')}`"
                    v-if="type === 'read' || contractManagerBlocked"
                    v-html="modelValue"
                />
                <input
                    v-if="type === 'txt-date'"
                    type="text"
                    :id="name"
                    :name="name"
                    class="form-text"
                    :placeholder="t('date.format')"
                    v-mask="t('date.mask')"
                    @input="changed"
                    :value="modelValue"
                    :size="size"
                    @blur="blurEvent"
                />
                <DatePicker
                    v-if="type === 'date'"
                    :placeholder="placeholder || t('date.format')"
                    :format="t('date.shortFormat')"
                    @date-picked="dateChanged"
                    :value="modelValue"
                    :min-date="dateMin ? new Date(dateMin) : undefined"
                    :max-date="dateMax ? new Date(dateMax) : undefined"
                />
                <VueTelInput
                    :defaultCountry="'fr'"
                    :onlyCountries="['fr']"
                    v-if="type === 'tel'"
                    :id="name"
                    :name="name"
                    mode="international"
                    :placeholder="placeholder"
                    :inputOptions="{ placeholder: '', autocomplete: 'off', id: name, name: name }"
                    :validCharactersOnly="true"
                    @on-input="telChanged"
                    :disabled="readonly"
                    :modelValue="modelValue"
                    :size="size"
                    @blur="blurEvent"
                />
                <input
                    :readonly="readonly"
                    v-if="type === 'text'"
                    :type="type"
                    :id="name"
                    :name="name"
                    :disabled="disabled"
                    :autocomplete="autocomplete ? 'on' : 'off'"
                    :required="required"
                    class="form-text"
                    :placeholder="placeholder"
                    @input="changed"
                    :value="modelValue"
                    :size="size"
                    @blur="blurEvent"
                    @focus="focusEvent"
                    @keydown.enter="$emit('enter')"
                />
                <input
                    :readonly="readonly"
                    v-if="type === 'number'"
                    type="text"
                    :id="name"
                    :name="name"
                    class="form-text"
                    :placeholder="placeholder"
                    v-mask="mask"
                    @input="changed"
                    :value="modelValue"
                    :size="size"
                    @blur="blurEvent"
                />
                <CurrencyInput
                    v-if="type === 'price'"
                    :value="modelValue"
                    @input="priceChanged"
                    :options="{
                        locale: 'fr-FR',
                        currency: 'EUR',
                        currencyDisplay: 'symbol',
                        precision: 2,
                        hideCurrencySymbolOnFocus: true,
                        hideGroupingSeparatorOnFocus: true,
                        hideNegligibleDecimalDigitsOnFocus: true,
                        autoDecimalDigits: false,
                        exportValueAsInteger: true,
                        autoSign: true,
                        useGrouping: true,
                    }"
                />
                <input
                    :readonly="readonly"
                    v-if="type === 'rawpassword'"
                    :type="'password'"
                    :id="name"
                    :name="name"
                    :autocomplete="autocomplete ? 'on' : 'off'"
                    class="form-text"
                    :placeholder="placeholder"
                    @input="changed"
                    :value="modelValue"
                    :size="size"
                    @blur="blurEvent"
                />
                <VuePassword
                    v-if="type === 'password'"
                    :classes="'form-text'"
                    :strength="passwordStrength(modelValue)"
                    :strengthMessages="['', '', '', '', '']"
                    @input="onPassword"
                    :value="modelValue"
                    type="password"
                />
                <select
                    v-if="type === 'select'"
                    :id="name"
                    :name="name"
                    class="form-select"
                    @change="changed"
                    :value="modelValue"
                    :readonly="readonly"
                    :placeholder="placeholder"
                    :size="size"
                >
                    <option
                        v-for="(option, index) in options"
                        :key="`option-${option.value}-${index}`"
                        :value="option.value"
                        :disabled="option.disabled"
                        :selected="option.selected || option.value === modelValue"
                        :hidden="option.hidden"
                    >
                        {{ option.label }}
                    </option>
                </select>
                <div class="template-select" v-if="type === 'template-area'">
                    <label v-if="!labelIcon && label" :for="name" v-html="theLabel" />
                    <div>
                        <label v-if="!labelIcon && label" :for="name" v-html="templateLabel" />
                        <select :id="name" class="form-select no-error" @change="changed" :value="modelValue" :placeholder="placeholder" :size="size">
                            <option
                                v-for="(option, index) in options"
                                :key="`option-${option.value}-${index}`"
                                :value="option.value"
                                :disabled="option.disabled"
                                :selected="option.selected"
                            >
                                {{ option.label }}
                            </option>
                        </select>
                    </div>
                </div>
                <textarea
                    v-if="type === 'textarea' || type === 'template-area'"
                    :id="name"
                    :name="name"
                    class="form-textarea"
                    @input="changed"
                    :value="modelValue"
                    :placeholder="placeholder"
                    :cols="size"
                    @blur="blurEvent"
                    :disabled="readonly"
                    :required="required"
                />
                <VueTimepicker
                    v-if="type === 'time'"
                    auto-scroll
                    manual-input
                    hide-clear-button
                    :minute-interval="5"
                    :disabled="readonly"
                    input-class="form-text"
                    :input-width="inputWidth"
                    :format="t('date.time2')"
                    :value="modelValue"
                    @input="timeChanged"
                />
            </template>
        </div>
        <PasswordStrength v-if="type === 'password'" :password="modelValue" :formSender="false" />
        <div class="meta" v-if="error">
            <ErrorComponent :error="error" />
        </div>
    </div>
</template>

<style lang="scss">
input {
    color: $neutral-900;
    &::placeholder {
        color: $grey;
    }
}

.form-select {
    border: 1px solid $grey2;
    &::placeholder {
        color: $grey;
    }
}

.form-textarea {
    border: 1px solid $neutral-300;
    color: $neutral-900;
    &:disabled {
        resize: none;
    }
    &::placeholder {
        color: $grey;
    }
}

.value.cm-blocked {
    outline: #f5f6fa solid 1.5px;
    outline-offset: -1.5px;
    color: #7a899e;
    font-weight: 500;
    font-size: 14px;
    height: 30px;
    line-height: 30px;
    border-radius: 30px !important;
    padding-left: 15px;
    display: inline-block;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.cm-blocked-explanation {
    margin-top: 5px;
    color: $grey2;
    font-size: 10px;
    line-height: 1.2em;
    padding: 0 10px;
}

.input {
    display: flex;
    flex-direction: column;
    gap: 8px;
}
</style>
